import React, { useState, useEffect } from "react";
import api from "../api";
import DataTable from "../components/DataTable";
import CustomButton from "../components/CustomButton";
import ModalForm from "../components/ModalForm";
import CustomTextInput from "../components/CustomTextInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";

export default function CarsPage() {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [name, setName] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [ownership, setOwnership] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Obtener el ordenamiento actual de la URL
  const currentSort = searchParams.get("ordering") || "";
  const currentSearch = searchParams.get("search") || "";

  const handleSortChange = (newSort) => {
    // Actualizar la URL con el nuevo ordenamiento
    setSearchParams({ ordering: newSort, search: currentSearch });
  };

  const handleSearchChange = (newSearch) => {
    // Actualizar la URL con el nuevo valor de búsqueda
    setSearchParams({ ordering: currentSort, search: newSearch });
  };

  const columnsConfig = [
    { field: "name", headerName: "Nombre del vehículo", sortable: true },
    { field: "licensePlate", headerName: "Chapa", sortable: true },
    {
      field: "ownership",
      headerName: "Propiedad",
      sortable: true,
    },
    {
      field: "vehicleType",
      headerName: "Tipo de vehículo",
      sortable: true,
    },
    {
      field: "status",
      headerName: "Estado actual",
      sortable: true,
    },
  ];

  const ownershipOptions = [
    { value: 1, label: "Aquila" },
    { value: 2, label: "Sixt" },
  ];

  const vehicleTypeOptions = [
    { value: 1, label: "Alta Gamma" },
    { value: 2, label: "Estándar" },
  ];

  const statusOptions = [
    { value: 1, label: "Disponible" },
    { value: 2, label: "En uso" },
    { value: 3, label: "En mantenimiento" },
  ];

  useEffect(() => {
    async function fetchVehicles() {
      try {
        setLoading(true);
        const response = await api.vehicles.getMulti(currentSort);

        const vehiclesWithLabels = response.map((vehicle) => ({
          ...vehicle,
          ownership:
            ownershipOptions.find((o) => o.value === vehicle.ownership)
              ?.label || "Desconocido",
          vehicleType:
            vehicleTypeOptions.find((v) => v.value === vehicle.vehicleType)
              ?.label || "Desconocido",
          status:
            statusOptions.find((s) => s.value === vehicle.status)?.label ||
            "Desconocido",
        }));

        setData(vehiclesWithLabels);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchVehicles();
  }, [currentSort]);

  // Función para manejar el click en "Detalles"
  const handleDetailsClick = (carId) => {
    navigate(`/cars/${carId}`); // Redirige a la página de detalles del vehiculo
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const newVehicle = {
      name,
      licensePlate,
      ownership,
      vehicleType,
      status,
    };

    try {
      const createdVehicle = await api.vehicles.create(newVehicle);

      // Actualizar localmente el estado
      const ownershipLabel =
        ownershipOptions.find((o) => o.value === createdVehicle.ownership)
          ?.label || "Desconocido";
      const vehicleTypeLabel =
        vehicleTypeOptions.find((v) => v.value === createdVehicle.vehicleType)
          ?.label || "Desconocido";
      const statusLabel =
        statusOptions.find((s) => s.value === createdVehicle.status)?.label ||
        "Desconocido";

      const updatedVehicle = {
        ...createdVehicle,
        ownership: ownershipLabel,
        vehicleType: vehicleTypeLabel,
        status: statusLabel,
      };

      setData((prevData) => [...prevData, updatedVehicle]);

      // Cerrar el modal y reiniciar el formulario
      setIsModalOpen(false);
      setName("");
      setLicensePlate("");
      setOwnership("");
      setVehicleType("");
      setStatus("");
    } catch (error) {
      if (error.message) {
        setFieldErrors(error.message);
      } else {
        setError("Error al crear el vehículo. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  return (
    <Layout>
      <div
        className={`p-8 space-y-10 bg-[#F8F9FB] transition-opacity ease-in-out`}
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
          Vehículos
        </h1>
        <div className="flex justify-between items-center">
          <p className="poppins-regular text-right text-[#595A5C] text-xs tracking-wider ">
            {data.length} vehículos registrados
          </p>
          <CustomButton
            text="Agregar vehículo"
            onClick={() => setIsModalOpen(true)}
            icon={true}
          />
        </div>

        {/*<p className="text-right text-[#595A5C] text-xs tracking-wider ">
            {data.length} vehículos registrados
          </p>*/}
        <DataTable
          key={data.length}
          columnsConfig={columnsConfig}
          data={data}
          loading={loading}
          onActionClick={(row) => handleDetailsClick(row.id)}
          onSortChange={handleSortChange}
          currentSort={currentSort}
          currentSearch={currentSearch}
          onSearchChange={(e) => handleSearchChange(e.target.value)}
        />
        <ModalForm
          title="Agregar un nuevo vehículo"
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleFormSubmit}
          textBtn="Agregar vehículo"
        >
          {error && <p className="text-red-600 mb-4">{error}</p>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <CustomTextInput
                label="Nombre del vehículo"
                placeholder="Ingresa el nombre del vehículo"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isRequired
              />
              {fieldErrors.name && (
                <p className="text-red-600 text-sm">{fieldErrors.name[0]}</p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Chapa del vehiculo"
                placeholder="Ingresa la chapa del vehículo"
                type="text"
                value={licensePlate}
                onChange={(e) => setLicensePlate(e.target.value)}
                isRequired
              />
              {fieldErrors.licensePlate && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.licensePlate[0]}
                </p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Propiedad de"
                placeholder="Selecciona al propietario"
                type="select"
                value={ownership}
                onChange={(e) => setOwnership(e.target.value)}
                options={ownershipOptions}
                isRequired
              />
              {fieldErrors.ownership && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.ownership[0]}
                </p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Tipo de vehículo"
                placeholder="Selecciona el tipo de vehículo"
                type="select"
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                options={vehicleTypeOptions}
                isRequired
              />
              {fieldErrors.vehicleType && (
                <p className="text-red-600 text-sm">
                  {fieldErrors.vehicleType[0]}
                </p>
              )}
            </div>
            <div>
              <CustomTextInput
                label="Estado"
                placeholder="Selecciona el estado"
                type="select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                options={statusOptions}
                isRequired
              />
              {fieldErrors.status && (
                <p className="text-red-600 text-sm">{fieldErrors.status[0]}</p>
              )}
            </div>
          </div>
        </ModalForm>
      </div>
    </Layout>
  );
}
